import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { paymentService } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import './PaymentSharePage.css';

/**
 * Página pública para visualização compartilhada de pagamentos.
 * Não requer autenticação - ideal para viralização via WhatsApp.
 */
function PaymentSharePage() {
  const { paymentId } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPayment();
  }, [paymentId]);

  const loadPayment = async () => {
    try {
      setLoading(true);
      // Por padrão usa HYBRID para privacidade
      const data = await paymentService.getShareablePayment(paymentId, 'HYBRID');
      setPayment(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const getStatusColor = (status) => {
    if (status === 'CONFIRMED') return 'status-paid';
    if (status === 'PENDING') return 'status-pending';
    if (status === 'CANCELLED' || status === 'EXPIRED' || status === 'FAILED') return 'status-cancelled';
    return '';
  };

  const getStatusIcon = (status) => {
    if (status === 'CONFIRMED') return '✓';
    if (status === 'PENDING') return '⏱';
    if (status === 'CANCELLED' || status === 'EXPIRED' || status === 'FAILED') return '✗';
    return '?';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="payment-share-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Ops!</h2>
          <p>{error}</p>
          <a href="https://dividu.app" className="btn-download">
            Baixar Dividu
          </a>
        </div>
      </div>
    );
  }

  if (!payment) {
    return null;
  }

  return (
    <div className="payment-share-page">
      <div className="share-header">
        <div className="app-logo">
          <span className="logo-icon">💰</span>
          <span className="logo-text">Dividu</span>
        </div>
        <p className="app-tagline">Dividir despesas nunca foi tão fácil</p>
      </div>

      <div className="payment-card">
        <div className={`status-badge ${getStatusColor(payment.paymentStatus)}`}>
          <span className="status-icon">{getStatusIcon(payment.paymentStatus)}</span>
          <span className="status-text">{payment.paymentStatusDescription}</span>
        </div>

        <div className="payment-header">
          <h1 className="payment-amount">{formatCurrency(payment.amount)}</h1>
          {payment.platformFee > 0 && (
            <p className="platform-fee">+ {formatCurrency(payment.platformFee)} (taxa)</p>
          )}
        </div>

        <div className="payment-details">
          <div className="detail-row">
            <span className="detail-label">Grupo:</span>
            <span className="detail-value">{payment.groupName}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Pagador:</span>
            <span className="detail-value">
              {payment.payerName}
              {payment.privacyLevel === 'HYBRID' && (
                <span className="privacy-hint"> (iniciais)</span>
              )}
            </span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Recebedor:</span>
            <span className="detail-value">
              {payment.receiverName}
              {payment.privacyLevel === 'HYBRID' && (
                <span className="privacy-hint"> (iniciais)</span>
              )}
            </span>
          </div>

          {payment.description && (
            <div className="detail-row">
              <span className="detail-label">Descrição:</span>
              <span className="detail-value">{payment.description}</span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">Criado em:</span>
            <span className="detail-value">{formatDate(payment.createdAt)}</span>
          </div>

          {payment.paidAt && (
            <div className="detail-row">
              <span className="detail-label">Pago em:</span>
              <span className="detail-value">{formatDate(payment.paidAt)}</span>
            </div>
          )}

          {payment.manualPaid && (
            <div className="manual-status">
              <p className="manual-badge">
                ✓ Pagador confirmou pagamento
                {payment.manualPaidAt && ` em ${formatDate(payment.manualPaidAt)}`}
              </p>
            </div>
          )}

          {payment.manualReceived && (
            <div className="manual-status">
              <p className="manual-badge">
                ✓ Recebedor confirmou recebimento
                {payment.manualReceivedAt && ` em ${formatDate(payment.manualReceivedAt)}`}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="cta-section">
        <h3>Divida despesas com seus amigos!</h3>
        <p>Controle quem pagou, quem deve e simplifique os acertos do grupo.</p>
        <a href="https://dividu.app" className="btn-download">
          <span className="btn-icon">📱</span>
          Baixar Dividu
        </a>
      </div>

      <div className="share-footer">
        <p>Compartilhado via Dividu</p>
        <div className="privacy-info">
          <span className="privacy-icon">🔒</span>
          <span>Privacidade: {payment.privacyLevel === 'COMPLETE' ? 'Completa' : payment.privacyLevel === 'HYBRID' ? 'Parcial' : 'Anônima'}</span>
        </div>
      </div>
    </div>
  );
}

export default PaymentSharePage;
